import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { Trans } from '@lingui/react/macro';
import type { CarNotificationDefaultRecord } from '@navio/nsdk';
import { invalidateAfterUserGroupMemberMutation, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBadge, NoddiButton } from 'noddi-ui-web';
import { getUserDisplayName } from 'noddi-util';
import { useAuthContext, useIsGroupAdmin } from 'noddi-web';
import type { BookingSmsOptIns } from '../../hooks/useBookingSmsOptIns';

type Props = {
  userGroupId: number;
  /** Drivers of the booked cars who have booking SMS off, from the summary's car defaults. */
  silencedManagers: CarNotificationDefaultRecord[];
  smsOptIns: Pick<BookingSmsOptIns, 'userIds' | 'setOptIn'>;
};

/**
 * Organization bookings only: tells the booker that a car's driver has booking
 * SMS off, so nobody gets the confirmation. The booker can turn SMS on for this
 * booking; a group admin can also turn the driver's own setting back on.
 */
export const SilencedCarManagersWarning = ({ userGroupId, silencedManagers, smsOptIns }: Props) => {
  const { userData } = useAuthContext();
  const { isGroupAdmin } = useIsGroupAdmin(userGroupId, userData?.user.id);
  const {
    mutate: patchMember,
    isPending: isPatching,
    variables: patchVariables,
    error: patchError
  } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroupMember,
    queryConfig: { onSuccess: () => invalidateAfterUserGroupMemberMutation({ userGroupId }) }
  });

  if (silencedManagers.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-3'>
      {silencedManagers.map((manager) => {
        const { userId, userGroupMemberId } = manager;
        const name = getUserDisplayName(manager);
        const isOptedIn = smsOptIns.userIds.has(userId);

        return (
          <div key={userId} className='flex flex-col gap-2'>
            <NoddiBadge variant={isOptedIn ? 'success' : 'warning'} withCircle>
              {isOptedIn ? <Trans>SMS on for this booking</Trans> : <Trans>Driver SMS is off</Trans>}
            </NoddiBadge>
            <p className='text-sm text-muted-foreground'>
              {isOptedIn ? (
                <Trans>{name} will get SMS updates about this booking.</Trans>
              ) : (
                <Trans>{name} has turned off booking SMS and will not get updates about this booking.</Trans>
              )}
            </p>
            <div className='flex flex-wrap gap-2'>
              <NoddiButton size='small' variant='outline' onClick={() => smsOptIns.setOptIn(userId, !isOptedIn)}>
                {isOptedIn ? <Trans>Undo</Trans> : <Trans>Send SMS for this booking</Trans>}
              </NoddiButton>
              {isGroupAdmin && (
                <NoddiButton
                  size='small'
                  variant='outline'
                  loading={isPatching && patchVariables?.userGroupMemberId === userGroupMemberId}
                  disabled={isPatching}
                  onClick={() => patchMember({ userGroupMemberId, smsBookingNotifications: true })}
                >
                  <Trans>Always send booking SMS to {name}</Trans>
                </NoddiButton>
              )}
            </div>
          </div>
        );
      })}
      {patchError && <ApiErrorMessageWithTrans error={patchError} />}
    </div>
  );
};
